import { get } from 'lodash-es';
import { defineStore } from 'pinia';
import { decodeHTML } from '@/lib/utils';
import * as types from '@/lib/constants/store';

const creationsApiData = {
  url: '/creations',
  path: '/',
  mask: '{creations{id, assignment_id, name, thumbnail{url}, shares, references, state, options{type, is_template, template_type}, metadata{created_on, updated_on, deleted_on, published_on}, access_level, pages{thumbnail{url}}}, page, num_pages}',
  boardsKey: 'creations',
};

const draftsApiData = {
  url: '/user/artifacts',
  path: '',
  mask: null,
  boardsKey: 'artifacts',
};

const getRouteInfo = (filter, user) => {
  const routes = {
    MY_STUDIO: {
      ...draftsApiData,
      routeParams: {
        boards: {
          search_type: 'type',
          search_value: 'board',
        },
        slideshows: {
          search_type: 'type',
          search_value: 'slides',
        },
        lessons: {
          search_type: 'type',
          search_value: 'lesson',
        },
      },
    },
    MY_STUDIO_DEPRECATED: {
      ...creationsApiData,
      path: '/',
      routeParams: {
        boards: {
          search_type: 'user_id',
          search_value: user.id,
        },
        slideshows: {
          search_type: 'type',
          search_value: 'slides',
        },
        lessons: {
          search_type: 'type',
          search_value: 'lesson',
        },
      },
    },
    SHARED_WITH_ME: {
      ...creationsApiData,
      path: '/shared',
      routeParams: {
        boards: {
          search_type: 'user_id',
          search_value: user.id,
        },
        slideshows: {
          search_type: 'type',
          search_value: 'slides',
        },
        lessons: {
          search_type: 'type',
          search_value: 'lesson',
        },
      },
    },
    ASSIGNED: {
      ...creationsApiData,
      path: '/',
      routeParams: {
        assigned_from_me: {
          search_type: 'assigned_from_me',
        },
      },
    },
    TRASH: {
      ...draftsApiData,
      routeParams: {
        all: {
          is_deleted: true,
        },
        boards: {
          is_deleted: true,
          search_type: 'type',
          search_value: 'board',
        },
        slideshows: {
          is_deleted: true,
          search_type: 'type',
          search_value: 'slides',
        },
        lessons: {
          is_deleted: true,
          search_type: 'type',
          search_value: 'lesson',
        },
      },
    },
  };

  return routes[filter];
};

const useHomeStore = defineStore('studio-home', {
  state: () => ({
    activityTemplates: null,
    boards: null,
    totalPages: null,
    error: null,
    loading: false,
  }),
  actions: {
    async [types.GET_ACTIVITY_TEMPLATES]() {
      this.loading = true;

      try {
        const result = await this.apiProgressBar.get('/templates/activity');
        if (result && result.data) {
          // This template data is used in a couple places, so best to format it only once
          this.activityTemplates = result.data.templates.map((template) => ({
            ...template,
            canBeUsedWith: template.tags
              .filter((tag) => tag.type === 'Most Relevant Media Types')
              .map((tag) => tag.name),
            decodedTitle: decodeHTML(template.title),
            skills: template.tags
              .filter((tag) => tag.type === 'Thinking Skills')
              .map((tag) => tag.name),
          }));
        } else {
          throw result;
        }
      } catch (e) {
        this.error = get(e, 'response.data.meta.message', e);
      } finally {
        this.loading = false;
      }
    },
    async [types.GET_HOME_BOARDS](payload) {
      const {
        query,
        user,
      } = payload;

      let {
        sidebarFilter,
      } = payload;

      const sortFieldMap = {
        alphabetical: 'name',
        created_on: 'metadata.created_on',
        updated_on: 'metadata.updated_on',
        deleted_on: 'metadata.deleted_on',
      };

      this.error = null;
      this.loading = true;

      try {
        // check for cooke as fallback to the original API
        if (window && window.cookieStore && sidebarFilter === 'MY_STUDIO') {
          const useOldApi = await window.cookieStore.get('STUDIO_USE_V1_API');
          if (useOldApi && parseInt(useOldApi.value, 10) === 1) {
            sidebarFilter = 'MY_STUDIO_DEPRECATED';
          }
        }
        const routeInfo = getRouteInfo(sidebarFilter, user);

        const mergedQuery = {
          ...query,
          ...routeInfo.routeParams[query.filter || {}],
        };

        const params = {
          page: mergedQuery.page,
          search_text: mergedQuery.search || undefined,
          search_type: mergedQuery.search_type || undefined,
          search_value: mergedQuery.search_value || undefined,
          sort_field: sortFieldMap[mergedQuery.sort],
          sort_direction: mergedQuery.sortOrder,
          per_page: 20,
          is_deleted: mergedQuery.is_deleted || undefined,
        };

        const headers = {};
        if (routeInfo.mask) {
          headers['X-Fields'] = routeInfo.mask;
        }

        const url = `${routeInfo.url}${routeInfo.path}`;
        const result = await this.apiProgressBar.get(url, {
          params,
          headers,
        });

        if (result && result.data) {
          const { boardsKey } = routeInfo;
          this.boards = result.data[boardsKey].map((board) => {
            const returnVal = {
              access_level: ['MY_STUDIO', 'TRASH', 'MY_STUDIO_DEPRECATED'].includes(sidebarFilter) ? 'owner' : undefined,
              state: ['drafts', 'artifacts'].includes(boardsKey) ? 'draft' : 'creation',
              ...board,
            };
            // lets look for an id
            let id = null;
            let reference = board.references?.find((ref) => ref.reference_subtype === 'head');
            if (reference) {
              id = reference.reference_id;
            } else {
              reference = board.references?.find((ref) => ref.reference_subtype === 'revision');
              if (reference) {
                id = reference.reference_id;
              } else {
                reference = board.references?.find((ref) => ref.reference_subtype === 'asset');
                if (reference) {
                  id = reference.reference_id;
                }
              }
            }
            returnVal.id = id;
            return returnVal;
          });
          this.totalPages = result.data.num_pages;
        } else {
          throw result;
        }
      } catch (e) {
        this.error = get(e, 'response.data.meta.message', e);
      } finally {
        this.loading = false;
      }
    },
  },
});

export default useHomeStore;
